<template>
  <div class="rf">
    <p class="rf__lede">
      คืนเงินและเพิกถอนสิทธิ์ทันที — ใช้กับกรณีที่คลินิกร้องขอมาทางไลน์หรือโทร
      <strong>ผลมีทันที ไม่รอจบรอบ</strong> และย้อนกลับไม่ได้
    </p>

    <div class="rf__lookup">
      <label class="rf__label" for="rf-sub">เลข subscription</label>
      <div class="rf__lookup-row">
        <input
          id="rf-sub"
          v-model.trim="subscriptionId"
          class="form-control rf__input"
          type="text"
          inputmode="numeric"
          placeholder="เช่น 1842"
          @keyup.enter="loadQuote"
        />
        <b-button variant="outline-primary" :disabled="!subscriptionId || isLoading" @click="loadQuote">
          <b-spinner v-if="isLoading" small class="mr-1" />ดูยอดที่คืนได้
        </b-button>
      </div>
      <small class="rf__hint">
        หาเลขนี้ได้จากตาราง <code>billing_subscription</code> — ไม่ใช่เลขสาขาและไม่ใช่เลขคำสั่งซื้อ
      </small>
    </div>

    <b-alert v-if="loadError" variant="danger" show class="mt-3">{{ loadError }}</b-alert>

    <div v-if="quote" class="rf__body">
      <dl class="rf__facts">
        <div><dt>สาขา</dt><dd>#{{ quote.branchId }}</dd></div>
        <div><dt>แพ็ก</dt><dd>{{ quote.planCode }}</dd></div>
        <div><dt>สถานะ</dt><dd>{{ quote.status }}</dd></div>
        <div><dt>รอบ</dt><dd>{{ quote.startDate }} → {{ quote.endDate || "ไม่มีวันหมดอายุ" }}</dd></div>
        <div><dt>เก็บไปจริง</dt><dd>{{ quote.pricePaid }} บาท</dd></div>
        <div>
          <dt>วันที่เหลือ</dt>
          <dd>{{ quote.daysRemaining === null ? "—" : `${quote.daysRemaining} จาก ${quote.totalDays} วัน` }}</dd>
        </div>
      </dl>

      <b-alert v-if="quote.recurringCancellationPending" variant="warning" show class="rf__pending">
        แผนตัดเงินที่ผู้ให้บริการถืออยู่ <strong>ยังไม่ถูกยกเลิก</strong> — ระบบจะบันทึกไว้ให้ตามเก็บ
        แต่ต้องไปกดยกเลิกในพอร์ทัลของผู้ให้บริการด้วย ไม่งั้นบัตรใบนี้จะถูกตัดรอบถัดไป
      </b-alert>

      <div class="rf__suggest">
        <span class="rf__suggest-label">ระบบเสนอ</span>
        <button type="button" class="rf__chip" @click="amountBaht = String(quote.suggested.full)">
          เต็มจำนวน {{ quote.suggested.full }} บาท
        </button>
        <button type="button" class="rf__chip" @click="amountBaht = String(quote.suggested.prorated)">
          ตามวันที่เหลือ {{ quote.suggested.prorated }} บาท
        </button>
        <button type="button" class="rf__chip" @click="amountBaht = '0'">
          ไม่คืนเงิน (0 บาท)
        </button>
      </div>
      <small class="rf__hint">
        เป็นข้อเสนอ ไม่ใช่ตัวเลือกที่ปิดตาย — กรอกยอดเองได้ เพดานคือ {{ quote.maxRefundable }} บาท
      </small>

      <div class="rf__form">
        <div class="rf__field">
          <label class="rf__label" for="rf-amount">ยอดที่คืนจริง (บาท)</label>
          <input id="rf-amount" v-model.trim="amountBaht" class="form-control" type="number" min="0" :max="quote.maxRefundable" />
        </div>
        <div class="rf__field rf__field--wide">
          <label class="rf__label" for="rf-reason">เหตุผล (บังคับ)</label>
          <input
            id="rf-reason"
            v-model.trim="reason"
            class="form-control"
            type="text"
            maxlength="255"
            placeholder="เช่น ซื้อผิดแพ็ก / ระบบใช้ไม่ได้ 3 วัน"
          />
        </div>
        <div class="rf__field rf__field--wide">
          <label class="rf__label" for="rf-ref">
            เลขอ้างอิงการคืนเงินจากพอร์ทัลผู้ให้บริการ
            <span v-if="needsRef" class="rf__req">บังคับเมื่อคืนเงินจริง</span>
          </label>
          <input id="rf-ref" v-model.trim="providerRefundRef" class="form-control" type="text" placeholder="คัดจากพอร์ทัล 2C2P" />
          <small class="rf__hint">
            ต้องไปกดคืนเงินในพอร์ทัลก่อน แล้วเอาเลขมากรอก — เลขนี้คือหลักฐานว่าเงินออกจริง
            ไม่ใช่แค่บันทึกว่าตั้งใจจะคืน
          </small>
        </div>
      </div>

      <b-alert v-if="submitError" variant="danger" show class="mt-2">{{ submitError }}</b-alert>
      <b-alert v-if="result" variant="success" show class="mt-2">
        คืนเงินเรียบร้อย — ใบคืนเงิน {{ result.refundOrderNumber }} · {{ result.amountRefunded }} บาท ·
        สิทธิ์ถูกเพิกถอนแล้ว (สิ้นสุด {{ result.subscription.endDate }})
        <template v-if="result.recurringCancellationPending">
          <br /><strong>ยังต้องไปยกเลิกแผนตัดเงินในพอร์ทัลด้วย</strong>
        </template>
      </b-alert>

      <div class="rf__actions">
        <b-button variant="danger" :disabled="!canSubmit" @click="submit">
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          {{ isSubmitting ? "กำลังดำเนินการ…" : "คืนเงินและเพิกถอนสิทธิ์" }}
        </b-button>
        <span class="rf__warn">ทำแล้วย้อนกลับไม่ได้ ตรวจยอดและเลขอ้างอิงอีกครั้งก่อนกด</span>
      </div>
    </div>
  </div>
</template>

<script>
import SuperadminService from "@/services/SuperadminService";

export default {
  name: "BillingRefundPanel",
  data() {
    return {
      subscriptionId: "",
      quote: null,
      isLoading: false,
      loadError: "",
      amountBaht: "",
      reason: "",
      providerRefundRef: "",
      isSubmitting: false,
      submitError: "",
      result: null,
    };
  },
  computed: {
    amountNumber() {
      const n = Number(this.amountBaht);
      return Number.isFinite(n) ? n : NaN;
    },
    // เลขอ้างอิงจำเป็นเฉพาะตอนมีเงินเคลื่อนจริง — ยอด 0 คือการเพิกถอนเฉย ๆ
    needsRef() {
      return this.amountNumber > 0;
    },
    canSubmit() {
      if (this.isSubmitting || !this.quote) return false;
      if (!Number.isFinite(this.amountNumber) || this.amountNumber < 0) return false;
      if (this.amountNumber > this.quote.maxRefundable) return false;
      if (!this.reason) return false;
      if (this.needsRef && !this.providerRefundRef) return false;
      return true;
    },
  },
  methods: {
    // BE ตอบ { errorCode, errorMessage } ไม่ใช่ { message }
    apiMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.errorMessage || data.message)) || fallback;
    },
    async loadQuote() {
      this.isLoading = true;
      this.loadError = "";
      this.quote = null;
      this.result = null;
      this.submitError = "";
      try {
        const res = await SuperadminService.getRefundQuote(this.subscriptionId);
        this.quote = res.data.data || res.data;
        // ตั้งค่าเริ่มต้นเป็นยอดตามวันที่เหลือ ซึ่งเป็นคำตอบที่ถูกบ่อยที่สุด
        // แต่ยังแก้ได้ — ไม่ได้ล็อกไว้
        this.amountBaht = String(this.quote.suggested.prorated);
      } catch (err) {
        this.loadError = this.apiMessage(err, "อ่านข้อมูล subscription ไม่ได้");
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.isSubmitting = true;
      this.submitError = "";
      try {
        const res = await SuperadminService.refundSubscription(this.quote.subscriptionId, {
          amountBaht: this.amountNumber,
          reason: this.reason,
          providerRefundRef: this.providerRefundRef || undefined,
        });
        this.result = res.data.data || res.data;
        // โหลดใหม่เพื่อให้สถานะบนจอตรงกับความจริงหลังเพิกถอน
        const res2 = await SuperadminService.getRefundQuote(this.quote.subscriptionId);
        this.quote = res2.data.data || res2.data;
      } catch (err) {
        this.submitError = this.apiMessage(err, "คืนเงินไม่สำเร็จ");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.rf {
  max-width: 880px;

  &__lede {
    font-size: 14px;
    color: #475467;
  }
  &__lookup {
    margin-top: 12px;
  }
  &__lookup-row {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  &__input {
    max-width: 200px;
  }
  &__label {
    display: block;
    font-size: 12.5px;
    font-weight: 600;
    color: #475467;
    margin-bottom: 4px;
  }
  &__req {
    margin-left: 6px;
    font-weight: 500;
    color: #b42318;
  }
  &__hint {
    display: block;
    margin-top: 5px;
    color: #98a2b3;
  }
  &__body {
    margin-top: 20px;
    padding: 18px;
    border: 1px solid #e3e6eb;
    border-radius: 12px;
    background: #fff;
  }
  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px 20px;
    margin: 0 0 14px;

    dt {
      font-size: 12px;
      font-weight: 600;
      color: #98a2b3;
      margin: 0;
    }
    dd {
      margin: 0;
      font-size: 14px;
      color: #101828;
    }
  }
  &__pending {
    font-size: 13.5px;
  }
  &__suggest {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  &__suggest-label {
    font-size: 12.5px;
    font-weight: 600;
    color: #98a2b3;
  }
  &__chip {
    border: 1px solid #d8dde5;
    background: #f8f9fb;
    border-radius: 999px;
    padding: 5px 13px;
    font-size: 13px;
    color: #475467;
    cursor: pointer;

    &:hover {
      border-color: #007bff;
      color: #0056b3;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
    margin-top: 16px;
  }
  &__field--wide {
    grid-column: 1 / -1;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 18px;
  }
  &__warn {
    font-size: 12.5px;
    color: #b42318;
  }
}
</style>
